import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  FiShield, 
  FiMail, 
  FiLock, 
  FiBell, 
  FiGlobe,
  FiCreditCard,
  FiUsers,
  FiSave,
  FiRefreshCw,
  FiEye,
  FiEyeOff,
  FiCheckCircle,
  FiAlertCircle
} from 'react-icons/fi';
import { Helmet } from 'react-helmet-async';
import { GlassCard } from '../../components/ui/GlassCard';
import toast from 'react-hot-toast';

export const AdminSettingsPage = () => {
  const [activeTab, setActiveTab] = useState('general');
  const [showApiKey, setShowApiKey] = useState({});
  const [settings, setSettings] = useState({
    general: {
      siteName: 'Nitro',
      siteUrl: 'https://nitrologs.com',
      supportEmail: 'support@nitrologs.com',
      contactPhone: '+234 800 123 4567',
      address: 'Lagos, Nigeria',
      currency: 'NGN',
      timezone: 'Africa/Lagos'
    },
    security: {
      twoFactorAuth: false,
      sessionTimeout: '30',
      maxLoginAttempts: '5',
      passwordExpiry: '90',
      ipWhitelisting: false
    },
    email: {
      smtpHost: 'smtp.gmail.com',
      smtpPort: '587',
      smtpUser: 'notifications@nitrologs.com',
      smtpPassword: '********',
      senderName: 'Nitro',
      senderEmail: 'noreply@nitrologs.com'
    },
    payment: {
      paystackPublicKey: 'pk_test_************',
      paystackSecretKey: 'sk_test_************',
      flutterwavePublicKey: 'FLWPUBK-************',
      flutterwaveSecretKey: 'FLWSECK-************',
      minDeposit: '100',
      maxDeposit: '1000000',
      transactionFee: '0'
    },
    notifications: {
      emailNotifications: true,
      smsNotifications: false,
      pushNotifications: true,
      newUserAlert: true,
      newOrderAlert: true,
      newDepositAlert: true,
      lowBalanceAlert: false
    },
    platform: {
      maintenanceMode: false,
      allowRegistrations: true,
      requireEmailVerification: true,
      requirePhoneVerification: false,
      autoApproveAccounts: false,
      enableReviews: true
    }
  });

  const tabs = [
    { id: 'general', label: 'General', icon: FiGlobe },
    { id: 'security', label: 'Security', icon: FiShield },
    { id: 'email', label: 'Email', icon: FiMail },
    { id: 'payment', label: 'Payment', icon: FiCreditCard },
    { id: 'notifications', label: 'Notifications', icon: FiBell },
    { id: 'platform', label: 'Platform', icon: FiUsers }
  ];

  const handleSettingChange = (section, field, value) => {
    setSettings(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const handleSave = (section) => {
    // Here you would save to your backend
    toast.success(`${section.charAt(0).toUpperCase() + section.slice(1)} settings saved successfully!`);
  };

  const handleReset = (section) => {
    // Here you would reset to defaults
    toast.success(`${section.charAt(0).toUpperCase() + section.slice(1)} settings reset to defaults`);
  };

  const toggleApiKeyVisibility = (key) => {
    setShowApiKey(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.5 }
  };

  return (
    <>
      <Helmet>
        <title>Settings | Admin | Nitro Logs</title>
      </Helmet>

      <div className="min-h-screen py-12 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-luxury-50 via-white to-luxury-100">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <motion.div
            variants={fadeInUp}
            initial="initial"
            animate="animate"
            className="mb-8"
          >
            <h1 className="text-3xl font-display font-bold text-luxury-900 mb-2">
              Platform <span className="gradient-text">Settings</span>
            </h1>
            <p className="text-luxury-600">
              Configure and manage your platform settings
            </p>
          </motion.div>

          {/* Tabs */}
          <motion.div
            variants={fadeInUp}
            initial="initial"
            animate="animate"
            className="glass rounded-2xl p-2 mb-8 inline-flex flex-wrap"
          >
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center space-x-2 px-6 py-3 rounded-xl transition-all duration-300 ${
                    activeTab === tab.id
                      ? 'bg-gradient-to-r from-primary-500 to-secondary-500 text-white shadow-luxury'
                      : 'text-luxury-600 hover:text-luxury-900'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </motion.div>

          {/* Settings Panels */}
          <motion.div
            variants={fadeInUp}
            initial="initial"
            animate="animate"
            className="glass rounded-2xl p-8"
          >
            {/* General Settings */}
            {activeTab === 'general' && (
              <div className="space-y-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-semibold text-luxury-900">General Settings</h2>
                  <div className="flex space-x-3">
                    <button
                      onClick={() => handleReset('general')}
                      className="px-4 py-2 glass rounded-xl flex items-center space-x-2 hover:bg-primary-50 transition-colors"
                    >
                      <FiRefreshCw className="w-4 h-4" />
                      <span>Reset</span>
                    </button>
                    <button
                      onClick={() => handleSave('general')}
                      className="px-4 py-2 bg-primary-600 text-white rounded-xl flex items-center space-x-2 hover:bg-primary-700 transition-colors"
                    >
                      <FiSave className="w-4 h-4" />
                      <span>Save Changes</span>
                    </button>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-luxury-700 mb-2">
                      Site Name
                    </label>
                    <input
                      type="text"
                      value={settings.general.siteName}
                      onChange={(e) => handleSettingChange('general', 'siteName', e.target.value)}
                      className="input-luxury"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-luxury-700 mb-2">
                      Site URL
                    </label>
                    <input
                      type="url"
                      value={settings.general.siteUrl}
                      onChange={(e) => handleSettingChange('general', 'siteUrl', e.target.value)}
                      className="input-luxury"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-luxury-700 mb-2">
                      Support Email
                    </label>
                    <input
                      type="email"
                      value={settings.general.supportEmail}
                      onChange={(e) => handleSettingChange('general', 'supportEmail', e.target.value)}
                      className="input-luxury"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-luxury-700 mb-2">
                      Contact Phone
                    </label>
                    <input
                      type="tel"
                      value={settings.general.contactPhone}
                      onChange={(e) => handleSettingChange('general', 'contactPhone', e.target.value)}
                      className="input-luxury"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-luxury-700 mb-2">
                      Address
                    </label>
                    <input
                      type="text"
                      value={settings.general.address}
                      onChange={(e) => handleSettingChange('general', 'address', e.target.value)}
                      className="input-luxury"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-luxury-700 mb-2">
                      Currency
                    </label>
                    <select
                      value={settings.general.currency}
                      onChange={(e) => handleSettingChange('general', 'currency', e.target.value)}
                      className="input-luxury"
                    >
                      <option value="NGN">NGN - Nigerian Naira</option>
                      <option value="USD">USD - US Dollar</option>
                      <option value="EUR">EUR - Euro</option>
                      <option value="GBP">GBP - British Pound</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-luxury-700 mb-2">
                      Timezone
                    </label>
                    <select
                      value={settings.general.timezone}
                      onChange={(e) => handleSettingChange('general', 'timezone', e.target.value)}
                      className="input-luxury"
                    >
                      <option value="Africa/Lagos">Africa/Lagos (WAT)</option>
                      <option value="Africa/Nairobi">Africa/Nairobi (EAT)</option>
                      <option value="America/New_York">America/New_York (EST)</option>
                      <option value="Europe/London">Europe/London (GMT)</option>
                    </select>
                  </div>
                </div>
              </div>
            )}

            {/* Security Settings */}
            {activeTab === 'security' && (
              <div className="space-y-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-semibold text-luxury-900">Security Settings</h2>
                  <div className="flex space-x-3">
                    <button
                      onClick={() => handleReset('security')}
                      className="px-4 py-2 glass rounded-xl flex items-center space-x-2 hover:bg-primary-50 transition-colors"
                    >
                      <FiRefreshCw className="w-4 h-4" />
                      <span>Reset</span>
                    </button>
                    <button
                      onClick={() => handleSave('security')}
                      className="px-4 py-2 bg-primary-600 text-white rounded-xl flex items-center space-x-2 hover:bg-primary-700 transition-colors"
                    >
                      <FiSave className="w-4 h-4" />
                      <span>Save Changes</span>
                    </button>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-luxury-100 rounded-xl">
                    <div>
                      <p className="font-medium text-luxury-900">Two-Factor Authentication</p>
                      <p className="text-sm text-luxury-500">Require 2FA for admin accounts</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={settings.security.twoFactorAuth}
                        onChange={(e) => handleSettingChange('security', 'twoFactorAuth', e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-luxury-300 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                    </label>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-luxury-700 mb-2">
                        Session Timeout (minutes)
                      </label>
                      <input
                        type="number"
                        value={settings.security.sessionTimeout}
                        onChange={(e) => handleSettingChange('security', 'sessionTimeout', e.target.value)}
                        className="input-luxury"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-luxury-700 mb-2">
                        Max Login Attempts
                      </label>
                      <input
                        type="number"
                        value={settings.security.maxLoginAttempts}
                        onChange={(e) => handleSettingChange('security', 'maxLoginAttempts', e.target.value)}
                        className="input-luxury"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-luxury-700 mb-2">
                        Password Expiry (days)
                      </label>
                      <input
                        type="number"
                        value={settings.security.passwordExpiry}
                        onChange={(e) => handleSettingChange('security', 'passwordExpiry', e.target.value)}
                        className="input-luxury"
                      />
                    </div>
                  </div>

                  <div className="flex items-center justify-between p-4 bg-luxury-100 rounded-xl">
                    <div>
                      <p className="font-medium text-luxury-900">IP Whitelisting</p>
                      <p className="text-sm text-luxury-500">Restrict admin access to specific IPs</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={settings.security.ipWhitelisting}
                        onChange={(e) => handleSettingChange('security', 'ipWhitelisting', e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-luxury-300 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                    </label>
                  </div>
                </div>
              </div>
            )}

            {/* Payment Settings */}
            {activeTab === 'payment' && (
              <div className="space-y-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-semibold text-luxury-900">Payment Settings</h2>
                  <div className="flex space-x-3">
                    <button
                      onClick={() => handleReset('payment')}
                      className="px-4 py-2 glass rounded-xl flex items-center space-x-2 hover:bg-primary-50 transition-colors"
                    >
                      <FiRefreshCw className="w-4 h-4" />
                      <span>Reset</span>
                    </button>
                    <button
                      onClick={() => handleSave('payment')}
                      className="px-4 py-2 bg-primary-600 text-white rounded-xl flex items-center space-x-2 hover:bg-primary-700 transition-colors"
                    >
                      <FiSave className="w-4 h-4" />
                      <span>Save Changes</span>
                    </button>
                  </div>
                </div>

                <div className="space-y-6">
                  {/* Paystack */}
                  <div className="p-4 bg-luxury-100 rounded-xl">
                    <h3 className="font-semibold text-luxury-900 mb-4">Paystack Configuration</h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-luxury-700 mb-2">
                          Public Key
                        </label>
                        <div className="relative">
                          <input
                            type={showApiKey.paystackPublic ? 'text' : 'password'}
                            value={settings.payment.paystackPublicKey}
                            onChange={(e) => handleSettingChange('payment', 'paystackPublicKey', e.target.value)}
                            className="input-luxury pr-10"
                          />
                          <button
                            onClick={() => toggleApiKeyVisibility('paystackPublic')}
                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-luxury-400 hover:text-luxury-600"
                          >
                            {showApiKey.paystackPublic ? <FiEyeOff size={16} /> : <FiEye size={16} />}
                          </button>
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-luxury-700 mb-2">
                          Secret Key
                        </label>
                        <div className="relative">
                          <input
                            type={showApiKey.paystackSecret ? 'text' : 'password'}
                            value={settings.payment.paystackSecretKey}
                            onChange={(e) => handleSettingChange('payment', 'paystackSecretKey', e.target.value)}
                            className="input-luxury pr-10"
                          />
                          <button
                            onClick={() => toggleApiKeyVisibility('paystackSecret')}
                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-luxury-400 hover:text-luxury-600"
                          >
                            {showApiKey.paystackSecret ? <FiEyeOff size={16} /> : <FiEye size={16} />}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Transaction Limits */}
                  <div className="grid md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-luxury-700 mb-2">
                        Minimum Deposit (₦)
                      </label>
                      <input
                        type="number"
                        value={settings.payment.minDeposit}
                        onChange={(e) => handleSettingChange('payment', 'minDeposit', e.target.value)}
                        className="input-luxury"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-luxury-700 mb-2">
                        Maximum Deposit (₦)
                      </label>
                      <input
                        type="number"
                        value={settings.payment.maxDeposit}
                        onChange={(e) => handleSettingChange('payment', 'maxDeposit', e.target.value)}
                        className="input-luxury"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-luxury-700 mb-2">
                        Transaction Fee (%)
                      </label>
                      <input
                        type="number"
                        step="0.1"
                        value={settings.payment.transactionFee}
                        onChange={(e) => handleSettingChange('payment', 'transactionFee', e.target.value)}
                        className="input-luxury"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Platform Settings */}
            {activeTab === 'platform' && (
              <div className="space-y-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-semibold text-luxury-900">Platform Settings</h2>
                  <div className="flex space-x-3">
                    <button
                      onClick={() => handleReset('platform')}
                      className="px-4 py-2 glass rounded-xl flex items-center space-x-2 hover:bg-primary-50 transition-colors"
                    >
                      <FiRefreshCw className="w-4 h-4" />
                      <span>Reset</span>
                    </button>
                    <button
                      onClick={() => handleSave('platform')}
                      className="px-4 py-2 bg-primary-600 text-white rounded-xl flex items-center space-x-2 hover:bg-primary-700 transition-colors"
                    >
                      <FiSave className="w-4 h-4" />
                      <span>Save Changes</span>
                    </button>
                  </div>
                </div>

                <div className="space-y-4">
                  {/* Maintenance Mode */}
                  <div className="flex items-center justify-between p-4 bg-luxury-100 rounded-xl">
                    <div>
                      <p className="font-medium text-luxury-900">Maintenance Mode</p>
                      <p className="text-sm text-luxury-500">Put the platform in maintenance mode</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={settings.platform.maintenanceMode}
                        onChange={(e) => handleSettingChange('platform', 'maintenanceMode', e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-luxury-300 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                    </label>
                  </div>

                  {/* Allow Registrations */}
                  <div className="flex items-center justify-between p-4 bg-luxury-100 rounded-xl">
                    <div>
                      <p className="font-medium text-luxury-900">Allow Registrations</p>
                      <p className="text-sm text-luxury-500">Enable new user registrations</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={settings.platform.allowRegistrations}
                        onChange={(e) => handleSettingChange('platform', 'allowRegistrations', e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-luxury-300 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                    </label>
                  </div>

                  {/* Email Verification */}
                  <div className="flex items-center justify-between p-4 bg-luxury-100 rounded-xl">
                    <div>
                      <p className="font-medium text-luxury-900">Require Email Verification</p>
                      <p className="text-sm text-luxury-500">Users must verify their email address</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={settings.platform.requireEmailVerification}
                        onChange={(e) => handleSettingChange('platform', 'requireEmailVerification', e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-luxury-300 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                    </label>
                  </div>

                  {/* Auto Approve Accounts */}
                  <div className="flex items-center justify-between p-4 bg-luxury-100 rounded-xl">
                    <div>
                      <p className="font-medium text-luxury-900">Auto-Approve Accounts</p>
                      <p className="text-sm text-luxury-500">Automatically approve new account listings</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={settings.platform.autoApproveAccounts}
                        onChange={(e) => handleSettingChange('platform', 'autoApproveAccounts', e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-luxury-300 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                    </label>
                  </div>

                  {/* Enable Reviews */}
                  <div className="flex items-center justify-between p-4 bg-luxury-100 rounded-xl">
                    <div>
                      <p className="font-medium text-luxury-900">Enable Reviews</p>
                      <p className="text-sm text-luxury-500">Allow users to leave reviews</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={settings.platform.enableReviews}
                        onChange={(e) => handleSettingChange('platform', 'enableReviews', e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-luxury-300 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                    </label>
                  </div>
                </div>
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </>
  );
};